import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { ErrorState } from '@/components/ui/ErrorState';
import { Skeleton } from '@/components/ui/Skeleton';
import { RESOURCES_STRINGS } from '../constants/resources-strings';
import { FolderCard } from './FolderCard';
import type { ResourceFolderDto } from '@/lib/types/resources.types';

/** A multiple of three, so every page is a whole number of rows (FR-177). */
export const FOLDERS_PAGE_SIZE = 12;
/** One row of card-shaped placeholders while folders load. */
const SKELETON_CARD_COUNT = 3;

// Columns follow this card's own width, not the viewport's (A-193), exactly as
// the dashboard's Upcoming Events block does: three across when wide, stacked
// when narrow, and the 2:1 cover keeps every photo in proportion throughout.
const GRID_CLASSES = 'grid grid-cols-1 gap-4 @3xl:grid-cols-3';

interface ResourceFoldersSectionProps {
  folders: ResourceFolderDto[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
}

/** Card-shaped placeholder: a 2:1 cover block over a body of name and count lines. */
function FolderCardSkeleton() {
  return (
    <div
      aria-hidden="true"
      className="border-border-strong bg-surface-card flex flex-col overflow-hidden rounded-2xl border"
    >
      <Skeleton className="aspect-[2/1] w-full rounded-none" />
      <div className="flex items-start gap-3 p-4">
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <Skeleton className="h-5 w-3/4" />
          <Skeleton className="h-4 w-1/3" />
        </div>
        <Skeleton className="h-9 w-28" />
      </div>
    </div>
  );
}

/**
 * "Browse by Category" on the member Resources landing page (FR-177): the
 * heading and the folder grid inside one white card, aligned with the
 * dashboard's Upcoming Events block. Loading, error and empty states render
 * inside the same card. "View More" pages further folders in, with no
 * "View All" link and no count badge (A-189).
 */
export function ResourceFoldersSection({
  folders,
  isLoading,
  isError,
  onRetry,
}: ResourceFoldersSectionProps) {
  const [visibleCount, setVisibleCount] = useState(FOLDERS_PAGE_SIZE);
  const hasMore = visibleCount < folders.length;

  return (
    <Card shadow="soft" hoverEffect={false} className="w-full">
      <section aria-labelledby="browse-folders-heading" className="flex flex-col gap-6">
        <h2 id="browse-folders-heading" className="text-h3 text-navy">
          {RESOURCES_STRINGS.BROWSE_FOLDERS_HEADING}
        </h2>

        {isError && <ErrorState onRetry={onRetry} />}

        {!isError && isLoading && (
          <div className="@container">
            <div className={GRID_CLASSES}>
              {Array.from({ length: SKELETON_CARD_COUNT }, (_, i) => (
                <FolderCardSkeleton key={i} />
              ))}
            </div>
          </div>
        )}

        {!isError && !isLoading && folders.length === 0 && (
          <div className="py-12 text-center">
            <p className="text-ink font-semibold">{RESOURCES_STRINGS.FOLDERS_EMPTY.TITLE}</p>
            <p className="text-body mt-1 text-sm">{RESOURCES_STRINGS.FOLDERS_EMPTY.DESCRIPTION}</p>
          </div>
        )}

        {!isError && !isLoading && folders.length > 0 && (
          <>
            <div className="@container">
              <div className={GRID_CLASSES}>
                {folders.slice(0, visibleCount).map((folder) => (
                  <FolderCard key={folder.id} folder={folder} />
                ))}
              </div>
            </div>
            {hasMore && (
              <div>
                <Button
                  type="button"
                  variant="link"
                  onClick={() => setVisibleCount((c) => c + FOLDERS_PAGE_SIZE)}
                >
                  {RESOURCES_STRINGS.VIEW_MORE}
                </Button>
              </div>
            )}
          </>
        )}
      </section>
    </Card>
  );
}
